import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {jwtDecode} from 'jwt-decode'; // Ensure this is installed via npm
import { setAuthTokens, removeLocalData, getAccessToken, getRefreshToken } from '../Helpers/authHelper';

// Service-specific URLs from .env
const SERVICE_URLS = {
  auth_service: process.env.REACT_APP_AUTH_SERVICE_URL,
  kii_service: process.env.REACT_APP_KII_SERVICE_URL,
  survey_service: process.env.REACT_APP_SURVEY_SERVICE_URL,
  external_service: process.env.REACT_APP_EXTERNAL_SERVICE_URL,
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

function useApi(props = { isPublic: false }) {


  const { isPublic } = props;
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createAxiosInstance = useCallback((service = 'auth_service') => {
    const baseURL = SERVICE_URLS[service] || SERVICE_URLS.auth_service;

    const instance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    instance.interceptors.request.use((config) => {
      const token = getAccessToken();
      if (!isPublic && token) {
        config.headers['Authorization'] = `Bearer ${token.value}`;
      }
      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const refreshToken = getRefreshToken();

        if (error.response?.status === 401 && !originalRequest._retry && refreshToken) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return instance(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing = true;
          try {
            const refreshResponse = await axios.post(
              `${SERVICE_URLS.auth_service}/v1/auth/refresh-token`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${refreshToken.value}`,
                },
              }
            );

            const { access_token } = refreshResponse.data;
            const decodedToken = jwtDecode(access_token);

            setAuthTokens(access_token, null);

            originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
            instance.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

            processQueue(null, access_token);
            return instance(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            removeLocalData();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, []);


  const request = useCallback(
    async (method, url, requestData = {}, service = 'auth_service') => {
    
     
      setLoading(true);
      setError(null);
      try {
        const axiosInstance = createAxiosInstance(service);
        const response = await axiosInstance({
          url,
          method,
          ...requestData,
        });
        setData(response.data);
        return response.data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [createAxiosInstance]
  );

  return { data, loading, error, request };
}

export default useApi;
